// FSD lib/ segment: pure validation helpers for EOB claim forms.
// Lives at page level alongside service-line-calculations — EOB-specific logic.

import type { DeductionFormValues, PatientClaimFormValues, ServiceLineFormValues } from '../model'
import { computePaidAmount } from './service-line-calculations'

function isDeductionEmpty(d: DeductionFormValues): boolean {
  return d.amount === null && d.codeGroups.length === 0
}

/** Returns true when every field in the service line is null (numbers) or empty string (strings). */
export function isServiceLineEmpty(sl: ServiceLineFormValues): boolean {
  return (
    sl.serviceDate === '' &&
    sl.cptCode === '' &&
    sl.billedAmount === null &&
    sl.allowedAmount === null &&
    sl.discount === null &&
    sl.otherPlan === null &&
    isDeductionEmpty(sl.adjustments) &&
    isDeductionEmpty(sl.deductible) &&
    isDeductionEmpty(sl.coInsurance) &&
    isDeductionEmpty(sl.copayment) &&
    isDeductionEmpty(sl.nonCovered)
  )
}

/**
 * Returns true when the claim form passes all validation rules:
 * - totalPaid is set
 * - required string fields are non-empty
 * - non-empty service lines have billedAmount set
 * - totalPaid reconciles with sum of computed paid amounts
 */
export function isClaimValid(values: PatientClaimFormValues): boolean {
  if (values.totalPaid == null) return false
  if (!values.patientAccountNumber.trim()) return false
  if (!values.patientFirstName.trim()) return false
  if (!values.patientLastName.trim()) return false
  if (!values.claimNumber.trim()) return false
  if (!values.pageOfDoc.trim()) return false

  for (const sl of values.serviceLines) {
    if (!isServiceLineEmpty(sl) && sl.billedAmount === null) return false
  }

  const sumPaid = values.serviceLines.reduce((acc, sl) => acc + computePaidAmount(sl), 0)
  return values.totalPaid === sumPaid
}
